import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AssetAuditEvent, AssetAudit } from '@/types/models';

// =====================================================
// AUDIT EVENTS (the audit itself)
// =====================================================

export function useAuditEvents(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['audit-events', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('asset_audit_events')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as AssetAuditEvent[];
    },
    enabled: !!organizationId,
  });
}

export function useAuditEvent(auditEventId: string | undefined) {
  return useQuery({
    queryKey: ['audit-event', auditEventId],
    queryFn: async () => {
      if (!auditEventId) {
        throw new Error('No audit ID provided');
      }

      const { data, error } = await supabase
        .from('asset_audit_events')
        .select('*')
        .eq('id', auditEventId)
        .single();

      if (error) {
        console.error('[useAuditEvent] Error fetching audit:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Audit not found');
      }

      return data as AssetAuditEvent;
    },
    enabled: !!auditEventId,
  });
}

export function useCreateAuditEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      organization_id: string;
      name: string;
      audit_date: string | null;
      due_by_date: string | null;
      auditor_id: string | null;
      status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
    }) => {
      const { data: auditEvent, error } = await supabase
        .from('asset_audit_events')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return auditEvent as AssetAuditEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
}

export function useUpdateAuditEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<AssetAuditEvent> & { id: string }) => {
      const { data, error } = await supabase
        .from('asset_audit_events')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as AssetAuditEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
      queryClient.invalidateQueries({ queryKey: ['audit-event'] });
    },
  });
}

export function useDeleteAuditEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('asset_audit_events')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
}

// =====================================================
// ASSET AUDITS (individual assets in an audit)
// =====================================================

export function useAssetAudits(auditEventId: string | undefined) {
  return useQuery({
    queryKey: ['asset-audits', auditEventId],
    queryFn: async () => {
      if (!auditEventId) return [];

      const { data, error } = await supabase
        .from('asset_audits')
        .select(`
          *,
          asset:assets(*)
        `)
        .eq('audit_event_id', auditEventId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as AssetAudit[];
    },
    enabled: !!auditEventId,
  });
}

export function useAddAssetToAudit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      organization_id: string;
      audit_event_id: string;
      asset_id: string;
      site_id: string | null;
      location_id: string | null;
      status: 'pending' | 'found' | 'missing' | 'damaged';
      notes: string | null;
    }) => {
      // Check if asset already exists in this audit
      const { data: existing, error: checkError } = await supabase
        .from('asset_audits')
        .select('id')
        .eq('audit_event_id', data.audit_event_id)
        .eq('asset_id', data.asset_id)
        .maybeSingle();

      if (checkError) throw checkError;
      if (existing) {
        throw new Error('Asset already exists in this audit');
      }

      const { data: assetAudit, error } = await supabase
        .from('asset_audits')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return assetAudit as AssetAudit;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-audits'] });
    },
  });
}

export function useAddMultipleAssetsToAudit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      organization_id: string;
      audit_event_id: string;
      asset_ids: string[];
    }) => {
      // Check which assets already exist in this audit
      const { data: existing, error: checkError } = await supabase
        .from('asset_audits')
        .select('asset_id')
        .eq('audit_event_id', data.audit_event_id)
        .in('asset_id', data.asset_ids);

      if (checkError) throw checkError;

      const existingAssetIds = existing?.map((a) => a.asset_id) || [];
      const newAssetIds = data.asset_ids.filter(
        (id) => !existingAssetIds.includes(id)
      );

      if (newAssetIds.length === 0) {
        throw new Error('All selected assets already exist in this audit');
      }

      // Insert new asset audits
      const inserts = newAssetIds.map((asset_id) => ({
        organization_id: data.organization_id,
        audit_event_id: data.audit_event_id,
        asset_id,
        status: 'pending' as const,
        site_id: null,
        location_id: null,
        notes: null,
      }));

      const { data: assetAudits, error } = await supabase
        .from('asset_audits')
        .insert(inserts)
        .select();

      if (error) throw error;
      return {
        added: assetAudits as AssetAudit[],
        skipped: existingAssetIds.length,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-audits'] });
    },
  });
}

export function useUpdateAssetAudit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<AssetAudit> & { id: string }) => {
      const { data, error } = await supabase
        .from('asset_audits')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as AssetAudit;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-audits'] });
    },
  });
}

export function useRemoveAssetFromAudit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('asset_audits')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-audits'] });
    },
  });
}
